/**
 * QTC as 1Click actually settles it (blackbeard/observer#43).
 *
 * A 1Click swap of the asset is not a `token_diff` trade: a solver transfers
 * the asset to a one-off 1Click account for a buy, or receives it from one for
 * a sell, and the user's payment happens on their own chain — never on NEAR.
 * So each row is a real swap being filled, with its size, side and solver, and
 * no price, because there is no price on NEAR to show. The price solvers pay
 * to restock is the Ref pool's, shown with the price chart.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { IntentsFill } from '../api/generated/IntentsFill'
import type { IntentsFills } from '../api/generated/IntentsFills'
import { RequestFailed, fetchIntentsFills } from '../api/rest'
import { height as fmtHeight, shortAddress, tokens, utc, when } from '../lib/format'
import { useObserverInstance } from '../lib/observer-context'
import { href } from '../lib/routes'
import { Field } from './Field'

const LIVE_LINES = 12
const REFRESH_MS = 60_000

export function FillsPanel({ chain, symbol }: { chain: string; symbol: string }) {
  const [data, setData] = useState<IntentsFills | null>(null)
  const [older, setOlder] = useState<IntentsFill[]>([])
  const [error, setError] = useState<string | null>(null)
  const [live, setLive] = useState<{ key: number; fill: IntentsFill }[]>([])
  const nextKey = useRef(0)
  const observer = useObserverInstance()

  useEffect(() => {
    const controller = new AbortController()
    const load = () =>
      fetchIntentsFills(chain, null, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    setOlder([])
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain])

  useEffect(
    () =>
      observer.onIntentsFill((fill) => {
        const key = nextKey.current++
        setLive((rows) => [{ key, fill }, ...rows].slice(0, LIVE_LINES))
      }),
    [observer],
  )

  const asset = data?.asset_symbol ?? symbol
  const d = data?.asset_decimals ?? 0
  const rows = data ? [...data.fills, ...older] : []
  const cursor = data?.next ?? null
  const loadOlder = () => {
    if (cursor === null) return
    fetchIntentsFills(chain, cursor)
      .then((page) => {
        setOlder((r) => [...r, ...page.fills])
        setData((cur) => (cur ? { ...cur, next: page.next } : page))
      })
      .catch(() => {})
  }
  const range =
    data && data.indexed_from !== null && data.indexed_to !== null
      ? `NEAR blocks ${fmtHeight(data.indexed_from)}–${fmtHeight(data.indexed_to)}`
      : 'nothing read yet'
  const account = (a: string) => (a.length > 40 ? shortAddress(a) : a)

  const table = (list: IntentsFill[], keyOf: (f: IntentsFill, i: number) => string) => (
    <div className="scroll-x">
      <table className="board">
        <caption className="visually-hidden">1Click fills of {asset}, newest first</caption>
        <thead>
          <tr>
            <th scope="col" className="left">
              Settled
            </th>
            <th scope="col" className="left">
              Side
            </th>
            <th scope="col">{asset}</th>
            <th scope="col" className="left">
              Solver
            </th>
            <th scope="col" className="left">
              1Click account
            </th>
            <th scope="col" className="left">
              Tx
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((f, i) => (
            <tr key={keyOf(f, i)}>
              <td className="left numeral" title={utc(f.at)}>
                {when(f.at)}
              </td>
              <td className="left">{f.side === 'buy' ? 'bought' : 'sold'}</td>
              <td className="numeral">{tokens(f.amount, d)}</td>
              <td className="left">{f.solver}</td>
              <td className="left" title={f.account}>
                <Link to={href({ chain, intentsAccount: f.account })}>{account(f.account)}</Link>
              </td>
              <td className="left">
                <Link to={href({ chain, nearTx: f.tx_hash })} title={f.tx_hash}>
                  {f.tx_hash.slice(0, 8)}…
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">1Click fills</h2>
          <span className="eyebrow">{range}</span>
        </div>
        {error && <p className="empty">{error}</p>}
        {!error && !data && <p className="empty">Reading…</p>}
        {data && (
          <div className="node-grid">
            <Field
              label="Bought"
              value={`${tokens(data.bought, d)} ${asset}`}
              note={`${fmtHeight(data.buys)} fills in the last ${data.hours} h`}
            />
            <Field
              label="Sold"
              value={`${tokens(data.sold, d)} ${asset}`}
              note={`${fmtHeight(data.sells)} fills in the last ${data.hours} h`}
            />
            <Field
              label="Solvers"
              value={`${data.solvers.length}`}
              note="accounts counted as filling"
              title={data.solvers.join('\n')}
            />
          </div>
        )}
        {live.length > 0 &&
          table(
            live.map((l) => l.fill),
            (_, i) => `live-${live[i]?.key}`,
          )}
        {data && rows.length === 0 && (
          <p className="empty">No {asset} has been filled through 1Click in the blocks read.</p>
        )}
        {rows.length > 0 &&
          table(rows, (f) => `${f.tx_hash}-${f.near_height}-${f.amount}-${f.side}`)}
        {cursor !== null && (
          <div className="pager">
            <button className="segmented panel-button" onClick={loadOlder}>
              Older →
            </button>
          </div>
        )}
        <p className="panel-note">
          This is how 1Click settles {asset}: a solver transfers it to a one-off 1Click account when
          a user buys, or receives it from one when a user sells. Each row is one such swap, with no
          price, because the user pays on their own chain and that payment never appears on NEAR.
          The 1Click account passing a buy on is not counted again. The price solvers pay to restock
          is on the <Link to={href({ chain, market: 'ref-dcl' })}>Ref Finance DCL market</Link>.
        </p>
      </section>
    </>
  )
}
